import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import {
  accountFormDefaultValues,
  TAccountForm,
  ZAccountForm,
  AccountVerificationProvider,
  useGuestDepositReturnAccount,
  convertDepositReturnAccountToFormValues,
  AccountValidationProvider,
  createAccountValidation,
} from '@/features/update-account'
import { cn } from '@/shared/lib'
import { Button, Dialog, Form } from '@/shared/ui'

import { DepositReturnAccount } from './deposit-return-account'
import { UpdateDepositReturnAccountButton } from './update-button'

type Props = {
  contractId: number
  onSuccess?: () => void
  onClose?: () => void
}

export const UpdateDepositReturnAccountForm = ({
  contractId,
  onSuccess,
  onClose,
}: Props) => {
  const t = useTranslations('deposit_information')

  const { depositReturnAccount } = useGuestDepositReturnAccount({
    contractId,
  })

  const schema = useMemo(
    () => ZAccountForm.superRefine(createAccountValidation(t)),
    [t],
  )

  const form = useForm<TAccountForm>({
    resolver: zodResolver(schema),
    defaultValues: accountFormDefaultValues,
  })

  const isFormEditable = depositReturnAccount?.isEditAvailable ?? true

  useEffect(() => {
    const convertedValues =
      convertDepositReturnAccountToFormValues(depositReturnAccount)

    if (convertedValues) {
      form.reset(convertedValues)
    }
  }, [depositReturnAccount, form])

  return (
    <Form.Root
      form={form}
      className={cn('flex grow flex-col overflow-auto', {
        'h-[682px]': isFormEditable,
      })}
    >
      <AccountVerificationProvider>
        <AccountValidationProvider>
          <Dialog.Body className="grow px-6">
            <DepositReturnAccount contractId={contractId} />
          </Dialog.Body>
          <Dialog.Footer className="flex-row gap-2.5 p-6">
            <Dialog.Close asChild>
              <Button
                type="button"
                variant="solid"
                color="secondary"
                size="large"
                fullWidth
                onClick={onClose}
              >
                {t('button.close')}
              </Button>
            </Dialog.Close>
            {isFormEditable && (
              <UpdateDepositReturnAccountButton
                contractId={contractId}
                onSuccess={onSuccess}
              />
            )}
          </Dialog.Footer>
        </AccountValidationProvider>
      </AccountVerificationProvider>
    </Form.Root>
  )
}
